import { useEffect, useState } from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { CONFIG } from '~/config';

import { ExternalRestClient } from '~/modules/requests';

type LocationDetails = { city?: string; country?: string; iata?: string; title?: string };
type ReferenceLocation = {
  code?: string;
  name?: string;
  name_translations?: Record<string, string>;
  city_name?: string;
  country_name?: string;
  country_code?: string;
};
type SearchParams = {
  origin?: string;
  destination?: string;
  originDetails?: LocationDetails;
  destinationDetails?: LocationDetails;
  departureDate?: string;
  returnDate?: string;
  travelers?: number;
};

function countryName(value?: string) {
  if (!value) {
    return '';
  }

  if (!/^[a-z]{2}$/i.test(value)) {
    return value;
  }

  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(value.toUpperCase()) || value;
  } catch {
    return value;
  }
}

function useLocationLabel(code?: string, details?: LocationDetails) {
  const [resolved, setResolved] = useState<{ code: string; label: string } | null>(null);
  const city = details?.city;
  const country = countryName(details?.country);
  const complete = Boolean(city && !/^[A-Z]{3}$/.test(city) && country);

  useEffect(() => {
    let active = true;

    if (complete || !code || !/^[a-z]{3}$/i.test(code)) {
      return undefined;
    }

    const normalized = code.toUpperCase();

    const lookup = async () => {
      const results = await Promise.all(
        ['cities', 'airports'].map(async dataset => {
          const response = await ExternalRestClient.get<
            { data?: ReferenceLocation[] } | ReferenceLocation[]
          >(
            `${CONFIG.env.apiBaseUrl}v2/reference-data/${dataset}?search=${encodeURIComponent(normalized)}&limit=8`,
          );
          const records = Array.isArray(response.data) ? response.data : response.data?.data || [];
          const record = records.find(item => item.code?.toUpperCase() === normalized);

          if (!record) {
            return null;
          }

          return {
            city: record.city_name || record.name_translations?.en || record.name,
            country: countryName(record.country_name || record.country_code),
          };
        }),
      );
      const location = results.find(Boolean);

      if (active && location?.city) {
        setResolved({ code, label: [location.city, location.country].filter(Boolean).join(', ') });
      }
    };

    lookup().catch(() => {
      /* Keep the order's location information when reference data is unavailable. */
    });

    return () => {
      active = false;
    };
  }, [code, complete]);

  if (complete) {
    return `${city}, ${country}`;
  }

  if (resolved && resolved.code === code) {
    return resolved.label;
  }

  return [city || details?.title || code || 'Not provided', country].filter(Boolean).join(', ');
}

export function formatOrderTravelDate(value?: string) {
  if (!value || !dayjs(value).isValid()) {
    return 'Not provided';
  }

  return dayjs(value).format('dddd, D MMMM YYYY');
}

export default function ItineraryOrderSummary({
  searchParams = {},
}: {
  searchParams?: SearchParams;
}) {
  const origin = useLocationLabel(searchParams.origin, searchParams.originDetails);
  const destination = useLocationLabel(searchParams.destination, searchParams.destinationDetails);
  const isReturn = Boolean(searchParams.returnDate);
  const travelers = Number(searchParams.travelers);

  return (
    <Box aria-label="Your trip summary" component="section">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography component="h2" fontWeight={700} variant="h6">
          Your trip at a glance
        </Typography>
        <Chip label={isReturn ? 'Return trip' : 'One-way trip'} size="small" />
      </Box>
      <Grid container spacing={2.5}>
        <Grid item md={6} xs={12}>
          <Typography color="text.secondary" variant="body2">
            From
          </Typography>
          <Typography fontWeight={600}>{origin}</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
            To
          </Typography>
          <Typography fontWeight={600}>{destination}</Typography>
        </Grid>
        <Grid item md={4} sm={8} xs={12}>
          <Typography color="text.secondary" variant="body2">
            Departure
          </Typography>
          <Typography fontWeight={600}>
            {formatOrderTravelDate(searchParams.departureDate)}
          </Typography>
          {isReturn ? (
            <Box sx={{ mt: 1 }}>
              <Typography color="text.secondary" variant="body2">
                Return
              </Typography>
              <Typography fontWeight={600}>
                {formatOrderTravelDate(searchParams.returnDate)}
              </Typography>
            </Box>
          ) : null}
        </Grid>
        <Grid item md={2} sm={4} xs={12}>
          <Typography color="text.secondary" variant="body2">
            Travellers
          </Typography>
          <Typography fontWeight={600}>
            {travelers > 0 ? `${travelers} traveller${travelers === 1 ? '' : 's'}` : 'Not provided'}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
